import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import CloseSVG from 'resources/img/icons/close-white.svg';
import CardSVG from 'resources/img/icons/card.svg';
import ArrowSVG from 'resources/img/icons/chevron-right.svg';
import PersonSVG from 'resources/img/icons/person.svg';
import LogOutSVG from 'resources/img/icons/logout.svg';
import VideoSVG from 'resources/img/icons/video.svg';
import CalendarSVG from 'resources/img/icons/calendar.svg';
import BellSVG from 'resources/img/icons/bell.svg';
import NoProfileSVG from 'resources/img/no-profile.svg';
import { Link } from 'react-router-dom';
import useUser from 'state/user';
import { getNotificationPermissionStatus } from 'utils/notifications';
import { UserTypes } from 'resources/constants/config';
import { IconButton } from '@mui/material';
import { AdminPanelSettings, Business as BusinessIcon } from '@mui/icons-material';
import { IMAGES_URL } from 'resources/constants/urls';

// ---------------------------------------------------------------------------
// SideMenu — rediseño moderno (26G)
// ---------------------------------------------------------------------------

const getInitials = (name = '') =>
    name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('');

const SideMenuContainer = styled.aside`
    position: absolute;
    top: 0;
    height: 100vh;
    height: 100dvh;
    width: 80vw;
    max-width: 300px;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    left: calc(0px - min(80vw, 300px));
    background-color: #fff;
    display: flex;
    flex-direction: column;
    border-radius: 0 24px 24px 0;
    overflow: hidden;
    z-index: 200;
    &.open {
        left: 0;
        box-shadow: 4px 0 24px rgba(0, 0, 0, 0.18);
    }
`;

// Header gradient
const DrawerHeader = styled.div`
    background: linear-gradient(140deg, #7cb900 0%, #a8c900 100%);
    padding: 20px 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const CloseRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
`;

const CloseButton = styled(IconButton)`
    background: rgba(255,255,255,0.2) !important;
    padding: 6px !important;
    img { width: 20px; height: 20px; }
    &:active { opacity: 0.7; }
`;

const UserRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const UserAvatar = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    overflow: hidden;
    border: 2.5px solid rgba(255,255,255,0.8);
    background: rgba(255,255,255,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 800;
    color: #1a2700;
    flex-shrink: 0;
    img { width: 100%; height: 100%; object-fit: cover; }
`;

const UserInfo = styled.div`
    flex: 1;
`;

const UserName = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: #1a2700;
    margin: 0 0 2px;
    line-height: 1.2;
`;

const UserType = styled.p`
    font-size: 12px;
    color: rgba(26,39,0,0.6);
    margin: 0;
    font-weight: 500;
`;

// Navigation
const Navigation = styled.nav`
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;

    ul {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    li {
        border-radius: 0;

        a, button {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 14px 20px;
            text-decoration: none;
            width: 100%;
            background: none;
            border: none;
            cursor: pointer;
            transition: background 0.15s;
            -webkit-tap-highlight-color: transparent;

            &:hover, &:active {
                background: #f5fae0;
            }

            img {
                width: 22px;
                height: 22px;
                opacity: 0.7;
                &.arrow {
                    width: 16px;
                    height: 16px;
                    opacity: 0.35;
                }
            }

            p {
                flex: 1;
                margin: 0;
                font-size: 15px;
                font-weight: 600;
                color: #2d2d2d;
                text-align: left;
            }
        }
    }
`;

const Divider = styled.div`
    height: 1px;
    background: #f0f0f0;
    margin: 4px 20px;
`;

const SideMenu = ({ menuOpen, toggleMenu }) => {
    const [user] = useUser();
    const [permissionStatus, setPermissionStatus] = useState('default');

    const checkStatus = async () => {
        setPermissionStatus(getNotificationPermissionStatus());
    };

    useEffect(() => {
        checkStatus();
    }, []);

    const name = `${user.current?.name ?? ''} ${user.current?.lastName ?? ''}`.trim();
    const role = user.current?.userType === UserTypes.THERAPIST ? 'Psicoterapeuta'
        : user.current?.userType === UserTypes.ADMIN ? 'Administrador'
        : user.current?.userType === UserTypes.BUSINESS ? 'Empresa'
        : 'Paciente';
    const profileImg = user.current?.profileImg;

    const isAdmin = user.current?.userType === UserTypes.ADMIN || user.current?.userType === 'admin';
    const isBusiness = user.current?.userType === UserTypes.BUSINESS || user.current?.userType === 'business';

    return (
        <>
        <SideMenuContainer className={menuOpen ? 'open' : ''}>
            {/* Header con avatar + nombre */}
            <DrawerHeader>
                <CloseRow>
                    <CloseButton onClick={toggleMenu}>
                        <img src={CloseSVG} alt='Cerrar' />
                    </CloseButton>
                </CloseRow>
                <UserRow>
                    <UserAvatar>
                        {profileImg
                            ? <img src={`${IMAGES_URL}${profileImg}`} alt="Perfil" />
                            : name ? getInitials(name) : <img src={NoProfileSVG} alt="Perfil" />
                        }
                    </UserAvatar>
                    <UserInfo>
                        <UserName>{name || 'Mi cuenta'}</UserName>
                        <UserType>{role}</UserType>
                    </UserInfo>
                </UserRow>
            </DrawerHeader>

            {/* Opciones de navegación */}
            <Navigation>
                <ul>
                    {isAdmin && (
                        <li>
                            <Link to="/admin" onClick={toggleMenu}>
                                <AdminPanelSettings sx={{ width: '22px', height: '22px', opacity: 0.7, color: '#2d2d2d' }} />
                                <p>Administración</p>
                                <img className='arrow' src={ArrowSVG} alt='→' />
                            </Link>
                        </li>
                    )}
                    {isBusiness && (
                        <li>
                            <Link to="/empresas" onClick={toggleMenu}>
                                <BusinessIcon sx={{ width: '22px', height: '22px', opacity: 0.7, color: '#2d2d2d' }} />
                                <p>Mi Empresa</p>
                                <img className='arrow' src={ArrowSVG} alt='→' />
                            </Link>
                        </li>
                    )}
                    <li>
                        <Link to="/perfil" onClick={toggleMenu}>
                            <img src={PersonSVG} alt='Perfil' />
                            <p>Perfil</p>
                            <img className='arrow' src={ArrowSVG} alt='→' />
                        </Link>
                    </li>
                    {user.current?.userType === UserTypes.THERAPIST && (
                        <li>
                            <Link to="/horario" onClick={toggleMenu}>
                                <img src={CalendarSVG} alt='Horario' />
                                <p>Horario disponible</p>
                                <img className='arrow' src={ArrowSVG} alt='→' />
                            </Link>
                        </li>
                    )}
                    <Divider />
                    <li>
                        <Link to="/videollamada" onClick={toggleMenu}>
                            <img src={VideoSVG} alt='Video' />
                            <p>Probar audio/video</p>
                            <img className='arrow' src={ArrowSVG} alt='→' />
                        </Link>
                    </li>
                    <li>
                        <Link to="/pagos" onClick={toggleMenu}>
                            <img src={CardSVG} alt='Pagos' />
                            <p>Pagos</p>
                            <img className='arrow' src={ArrowSVG} alt='→' />
                        </Link>
                    </li>
                    {permissionStatus === 'granted' || permissionStatus === 'denied' ? (
                        <li>
                            <Link to="/notificaciones" onClick={toggleMenu}>
                                <img src={BellSVG} alt='Notificaciones' />
                                {permissionStatus === 'granted' ? (
                                    <p style={{ color: '#7cb900' }}>Notificaciones activas ✓</p>
                                ) : (
                                    <p>Notificaciones</p>
                                )}
                                <img className='arrow' src={ArrowSVG} alt='→' />
                            </Link>
                        </li>
                    ) : (
                        <li>
                            <Link to="/notificaciones" onClick={toggleMenu}>
                                <img src={BellSVG} alt='Notificaciones' />
                                <p>Notificaciones</p>
                                <img className='arrow' src={ArrowSVG} alt='→' />
                            </Link>
                        </li>
                    )}
                    <Divider />
                    <li>
                        <Link to="/logout" onClick={toggleMenu}>
                            <img src={LogOutSVG} alt='Salir' />
                            <p>Cerrar Sesión</p>
                        </Link>
                    </li>
                </ul>
            </Navigation>
        </SideMenuContainer>
        </>
    );
};

export default SideMenu;
